import type { CSSProperties, HTMLAttributes } from 'react';
import { Link } from 'react-router-dom';
import type { TaskType } from '@econtractor/types';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'context/AuthContext';
import EToolTip from 'components/others/EToolTip';
import TaskRemindButton from '../../../shared/TaskRemindButton';
import TaskTypeChip from '../../../shared/TaskTypeChip';
import { formatDue, getRelatedLabel, isOverdueTask } from './helpers';
import type { KanbanRelatedTasksTarget } from './types';
import { tDirName } from 'utils/helpers/i18n/tDirName';
import { isManagerUser } from 'utils/helpers/access/hasUserAccess';
import SolarIcon from 'components/icons/SolarIcon';

const UserAvatar = ({
    name,
    picture,
    title,
}: {
    name?: string | null;
    picture?: string | null;
    title: string;
}) => {
    const src = picture || '/imgs/user.png';
    return (
        <EToolTip title={title}>
            <img
                className="task-kanban-card__avatar"
                src={src}
                alt={name || ''}
                onError={(e) => {
                    (e.currentTarget as HTMLImageElement).src = '/imgs/user.png';
                }}
            />
        </EToolTip>
    );
};

export interface KanbanCardBodyProps {
    task: TaskType;
    onOpen: (taskId: number) => void;
    onShowRelatedTasks: (target: KanbanRelatedTasksTarget) => void;
    dragHandleProps?: HTMLAttributes<HTMLElement>;
    style?: CSSProperties;
    className?: string;
}

const KanbanCardBody = ({
    task,
    onOpen,
    onShowRelatedTasks,
    dragHandleProps,
    style,
    className,
}: KanbanCardBodyProps) => {
    const { t } = useTranslation(['Tasks']);
    const { auth } = useAuth();
    const overdue = isOverdueTask(task);
    const dueLabel = formatDue(task);
    const related = getRelatedLabel(task);
    const executor = task.executorUser;
    const controller = task.controllerUser;
    const canOpenRelated = isManagerUser(auth?.user);
 
    return (
        <article
            className={[
                'task-kanban-card',
                overdue ? 'task-kanban-card--overdue' : undefined,
                Number(task.taskDeleted) ? 'task-kanban-card--deleted' : undefined,
                className,
            ]
                .filter(Boolean)
                .join(' ')}
            style={style}
            role="button"
            tabIndex={0}
            onClick={() => onOpen(task.ID)}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onOpen(task.ID);
                }
            }}
            {...dragHandleProps}
        >
            <div className="task-kanban-card__top">
                <TaskTypeChip
                    name={tDirName(t, task.taskTypeI18nKey, task.taskTypeName)}
                    icon={task.taskTypeIcon}
                    colors={task.taslTypeJson}
                    fontSize="0.68rem"
                />
                {overdue ? (
                    <span className="task-kanban-card__overdue-tag">
                        <SolarIcon name="exclamation-circle" className="me-1" />
                        {t('status.overdue')}
                    </span>
                ) : null}
            </div>

            <h6 className="task-kanban-card__title" title={task.taskName || undefined}>
                {task.taskName || '—'}
            </h6>

            {related ? (
                <div className="task-kanban-card__related-row">
                    {canOpenRelated ? (
                    <Link
                        to={related.to}
                        className="task-kanban-card__related"
                        title={related.title}
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                    >
                        <SolarIcon name={related.icon} />
                        <span>{related.label}</span>
                    </Link>
                    ) : (
                    <span className="task-kanban-card__related" title={related.title}>
                        <SolarIcon name={related.icon} />
                        <span>{related.label}</span>
                    </span>
                    )}
                    <button
                        type="button"
                        className="task-kanban-card__all-tasks"
                        onClick={(e) => {
                            e.stopPropagation();
                            onShowRelatedTasks({
                                leadId: related.leadId,
                                objectId: related.objectId,
                                contextName: related.label,
                            });
                        }}
                    >
                        {t('linkAllTasks')}
                    </button>
                </div>
            ) : null}

            <div className="task-kanban-card__footer">
                <div className="task-kanban-card__due">
                    {dueLabel ? (
                        <>
                            <SolarIcon name="calendar3" />
                            <span>{dueLabel}</span>
                        </>
                    ) : (
                        <span className="text-muted">—</span>
                    )}
                </div>

                <div className="task-kanban-card__footer-right">
                    <TaskRemindButton task={task} variant="kanban" />
                    <div className="task-kanban-card__avatars">
                        {executor?.ID ? (
                            <UserAvatar
                                name={executor.userName}
                                picture={executor.userPicture}
                                title={`${t('fields.executor')}: ${executor.userName || executor.userEmail || ''}`}
                            />
                        ) : null}
                        {controller?.ID && controller.ID !== executor?.ID ? (
                            <UserAvatar
                                name={controller.userName}
                                picture={controller.userPicture}
                                title={`${t('fields.controller')}: ${controller.userName || controller.userEmail || ''}`}
                            />
                        ) : null}
                    </div>
                </div>
            </div>
        </article>
    );
};

export default KanbanCardBody;
